import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericLoading } from 'components/ui/Loader';
import { NsKey } from 'enums/NsKey';
import { CorporateDto, getCorporate } from '../api/corporateApi';
import { CorporateInfoCard } from './CorporateInfoCard';
import { CorporateRolesCard } from './CorporateRolesCard';
import { CorporateBankAccountsManager } from './CorporateBankAccountsManager';
import { GouvernanceHeader } from './GouvernanceHeader';
import { PersonneMoraleProfileTabs } from 'back-office/shared/components/personnes-morales/PersonneMoraleProfileTabs';
import { getPersonneMoraleMembers } from 'features/access/api/personneMoraleAccessApi';
import { getHabilitationMatrix } from '../habilitations-tunnels/api/habilitationsTunnelsApi';

interface PlateformeGovernanceTabProps {
  plateformeId: string;
  /** Rafraîchit le header de la page (le nom = DisplayName de l'OU y est affiché). */
  onPlateformeUpdated?: () => void;
  /** Mode de gestion de la plateforme (GEREE / ADMINISTREE). */
  modeGestion?: string;
}

/**
 * Onglet « Gouvernance » de la page détail d'une plateforme : la personne
 * morale gérante (compagnie, courtier grossiste…) de bout en bout — identité,
 * rôles du compte, collaborateurs habilités et comptes bancaires. Fusion des
 * anciens onglets Informations et Gouvernance : même objet (la PM gérante),
 * même fetch, un seul état « pas de PM principale ».
 */
export const PlateformeGovernanceTab: React.FC<
  PlateformeGovernanceTabProps
> = ({ plateformeId, onPlateformeUpdated, modeGestion }) => {
  const { t } = useTranslation(NsKey.PersonneMorale);
  const [corporate, setCorporate] = useState<CorporateDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [collaborateursCount, setCollaborateursCount] = useState<number | null>(
    null
  );
  const [tunnelsCount, setTunnelsCount] = useState<number | null>(null);

  const fetchCorporate = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setCorporate(await getCorporate(plateformeId));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [plateformeId]);

  useEffect(() => {
    fetchCorporate();
  }, [fetchCorporate]);

  // Chiffres clés du héro — best-effort, l'onglet vit sans eux.
  useEffect(() => {
    if (!corporate) return;
    getPersonneMoraleMembers(corporate.personneMoraleId)
      .then(m => setCollaborateursCount(m.collaborators.length))
      .catch(() => setCollaborateursCount(null));
    getHabilitationMatrix(plateformeId)
      .then(m => setTunnelsCount(m.tunnels?.length ?? null))
      .catch(() => setTunnelsCount(null));
  }, [corporate, plateformeId]);

  if (loading) {
    return <GenericLoading isPageCentered loading />;
  }

  if (error || !corporate) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
        {t('governance.noPrincipal')}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* La réponse d'abord : qui gère la plateforme, et ses chiffres clés.
          Le héro fait office d'en-tête de la card — mis bord à bord via le
          variant wrapper, sans toucher au composant. */}
      <div className="[&_.pm-profile-hero]:rounded-none">
        <GouvernanceHeader
          corporate={corporate}
          collaborateursCount={collaborateursCount}
          tunnelsCount={tunnelsCount}
          modeGestion={modeGestion}
        />
      </div>

      {/* Puis le détail, en onglets : la fiche de la PM d'un côté, ses
          collaborateurs de l'autre — le layout est partagé avec la consultation
          rapide d'un intermédiaire (PersonneMoraleProfileTabs). */}
      <div className="px-6 pb-6 pt-4 shadow-inner">
        <PersonneMoraleProfileTabs
          personneMoraleId={corporate.personneMoraleId}
          membersCount={collaborateursCount}
          membersDescription={t('tabs.governingMembersDescription')}
          leftColumn={
            <>
              <CorporateInfoCard
                plateformeId={plateformeId}
                corporate={corporate}
                variant="identity"
                onSaved={() => {
                  fetchCorporate();
                  onPlateformeUpdated?.();
                }}
              />
              {/* Comptes bancaires : CRUD en place (personId = personneMoraleId) */}
              <CorporateBankAccountsManager
                personId={corporate.personneMoraleId}
              />
            </>
          }
          rightColumn={
            <>
              <CorporateInfoCard
                plateformeId={plateformeId}
                corporate={corporate}
                variant="contact"
                onSaved={fetchCorporate}
              />
              {/* Rôles : lecture par défaut, édition en place (compte ABP) */}
              <CorporateRolesCard
                plateformeId={plateformeId}
                assignedRoles={corporate.assignedRoles}
                assignableRoles={corporate.assignableRoles}
                nature={corporate.nature}
                sousNature={corporate.sousNature}
                modeGestion={modeGestion}
                onSaved={fetchCorporate}
              />
            </>
          }
        />
      </div>
    </div>
  );
};
